import type {
  IAnalyticsAutomationRunContext,
  IAnalyticsMessage,
  IAnalyticsMessagesParams,
  IAnalyticsQueryParams,
  IAnalyticsResponse,
  IAnalyticsScopeParams,
  IAutomationRunContextParams,
  IChatThreadWindowParams,
  IChatThreadWindowResponse,
  IDownloadAnalyticsMessageFileParams,
  IExportAnalyticsMessagesParams,
  IMessageContextParams,
  IMessageFileAnonymization,
  IMessageFileAnonymizationParams,
  IMessageModelAndProviderOptionsResponse,
  IMessagesSummary,
  IPaginatedAnalyticsMessagesResponse,
  IPreviewAnalyticsMessageFileParams,
  IRecentFlaggedMessagesParams,
  TMessageContextResponse,
} from "../types/";
import type { ApiClient } from "./client";

interface IAppendListProps {
  key: string;
  values: string[] | undefined;
}

type TMessagesFilterParams = Omit<
  IAnalyticsMessagesParams,
  "limit" | "offset" | "messageUuid"
>;

// NOTE: NSAI-7022 — the Messages list filters as query params, shared by the
// list request and its export.
const buildMessagesFilterSearchParams = (
  params: TMessagesFilterParams,
): URLSearchParams => {
  const searchParams = new URLSearchParams();
  const appendList = ({ key, values }: IAppendListProps): void => {
    values?.forEach((value) => searchParams.append(key, value));
  };

  if (params.sortColumn) searchParams.append("sortColumn", params.sortColumn);
  if (params.sortOrder) searchParams.append("sortOrder", params.sortOrder);
  if (params.hasAllMembersAnalyticsReadPermission) {
    appendList({ key: "memberUuid", values: params.memberUuids });
  }
  if (params.direction) searchParams.append("direction", params.direction);
  appendList({ key: "source", values: params.source });
  appendList({ key: "status", values: params.status });
  appendList({ key: "modelTitle", values: params.modelTitle });
  appendList({ key: "providerName", values: params.providerName });
  appendList({ key: "assistantName", values: params.assistantName });
  if (params.dateFrom) searchParams.append("dateFrom", params.dateFrom);
  if (params.dateTo) searchParams.append("dateTo", params.dateTo);
  if (params.projectUuid) {
    searchParams.append("projectUuid", params.projectUuid);
  }
  if (params.scope) searchParams.append("scope", params.scope);
  if (params.deletionStatus) {
    searchParams.append("deletionStatus", params.deletionStatus);
  }
  if (params.search) searchParams.append("search", params.search);

  return searchParams;
};

export class AnalyticsApi {
  constructor(private apiClient: ApiClient) {}

  async getUserAnalytics(
    params: IAnalyticsQueryParams,
  ): Promise<IAnalyticsResponse> {
    const {
      dateFrom,
      dateTo,
      memberUuids,
      hasAllMembersAnalyticsReadPermission,
      includeDeleted: shouldIncludeDeleted,
    } = params;
    const searchParams = new URLSearchParams();
    if (dateFrom) searchParams.append("dateFrom", dateFrom);
    if (dateTo) searchParams.append("dateTo", dateTo);
    if (hasAllMembersAnalyticsReadPermission && memberUuids?.length) {
      memberUuids.forEach((uuid) => searchParams.append("memberUuid", uuid));
    }
    // NOTE: The personal /analytics/dashboard DTO doesn't whitelist includeDeleted;
    // only forward it on the all-members route to avoid forbidNonWhitelisted 400s.
    if (hasAllMembersAnalyticsReadPermission && shouldIncludeDeleted) {
      searchParams.append("includeDeleted", "true");
    }
    searchParams.append(
      "tzOffsetMinutes",
      String(new Date().getTimezoneOffset()),
    );

    const endpoint = hasAllMembersAnalyticsReadPermission
      ? `/analytics/dashboard/all-members`
      : `/analytics/dashboard`;
    const response = await this.apiClient.get<IAnalyticsResponse>(
      `${endpoint}?${searchParams.toString()}`,
    );
    return response.data;
  }

  async getMessagesSummary(
    params: IAnalyticsQueryParams,
  ): Promise<IMessagesSummary> {
    const {
      dateFrom,
      dateTo,
      memberUuids,
      hasAllMembersAnalyticsReadPermission,
      includeDeleted: shouldIncludeDeleted,
    } = params;
    const searchParams = new URLSearchParams();
    if (dateFrom) searchParams.append("dateFrom", dateFrom);
    if (dateTo) searchParams.append("dateTo", dateTo);
    if (hasAllMembersAnalyticsReadPermission && memberUuids?.length) {
      memberUuids.forEach((uuid) => searchParams.append("memberUuid", uuid));
    }
    if (hasAllMembersAnalyticsReadPermission && shouldIncludeDeleted) {
      searchParams.append("includeDeleted", "true");
    }
    searchParams.append(
      "tzOffsetMinutes",
      String(new Date().getTimezoneOffset()),
    );

    const endpoint = hasAllMembersAnalyticsReadPermission
      ? `/analytics/summary/all-members`
      : `/analytics/summary`;
    const query = searchParams.toString();
    const response = await this.apiClient.get<IMessagesSummary>(
      `${endpoint}${query ? `?${query}` : ""}`,
    );
    return response.data;
  }

  async getRecentFlaggedMessages(
    params: IRecentFlaggedMessagesParams,
  ): Promise<IAnalyticsMessage[]> {
    const {
      limit,
      dateFrom,
      dateTo,
      memberUuids,
      hasAllMembersAnalyticsReadPermission,
      includeDeleted: shouldIncludeDeleted,
    } = params;
    const searchParams = new URLSearchParams();
    if (limit) searchParams.append("limit", limit.toString());
    if (dateFrom) searchParams.append("dateFrom", dateFrom);
    if (dateTo) searchParams.append("dateTo", dateTo);
    if (hasAllMembersAnalyticsReadPermission && memberUuids?.length) {
      memberUuids.forEach((uuid) => searchParams.append("memberUuid", uuid));
    }
    if (hasAllMembersAnalyticsReadPermission && shouldIncludeDeleted) {
      searchParams.append("includeDeleted", "true");
    }
    searchParams.append(
      "tzOffsetMinutes",
      String(new Date().getTimezoneOffset()),
    );

    const endpoint = hasAllMembersAnalyticsReadPermission
      ? `/analytics/recent-flagged/all-members`
      : `/analytics/recent-flagged`;
    const response = await this.apiClient.get<IAnalyticsMessage[]>(
      `${endpoint}?${searchParams.toString()}`,
    );
    return response.data;
  }

  async getMessageContext(
    params: IMessageContextParams,
  ): Promise<TMessageContextResponse> {
    const {
      chatUuid,
      messageUuid,
      projectUuid,
      includeDeleted: shouldIncludeDeleted,
    } = params;
    const searchParams = new URLSearchParams();
    searchParams.append("chatUuid", chatUuid);
    searchParams.append("messageUuid", messageUuid);
    if (projectUuid) searchParams.append("projectUuid", projectUuid);
    if (shouldIncludeDeleted) searchParams.append("includeDeleted", "true");
    const response = await this.apiClient.get<TMessageContextResponse>(
      `/analytics/message-context?${searchParams.toString()}`,
    );
    return response.data;
  }

  // NOTE: NSAI-6408 — details of an automation-run row in Messages.
  async getAutomationRunContext(
    params: IAutomationRunContextParams,
  ): Promise<IAnalyticsAutomationRunContext> {
    const { runUuid, automationType, projectUuid } = params;
    const searchParams = new URLSearchParams();
    searchParams.append("runUuid", runUuid);
    searchParams.append("automationType", automationType);
    if (projectUuid) searchParams.append("projectUuid", projectUuid);
    const response = await this.apiClient.get<IAnalyticsAutomationRunContext>(
      `/analytics/automation-run-context?${searchParams.toString()}`,
    );
    return response.data;
  }

  async getChatThreadWindow(
    params: IChatThreadWindowParams,
  ): Promise<IChatThreadWindowResponse> {
    const {
      chatUuid,
      anchorMessageUuid,
      projectUuid,
      limit,
      direction,
      cursorCreatedAt,
      cursorUuid,
      includeDeleted: shouldIncludeDeleted,
    } = params;
    const searchParams = new URLSearchParams();
    searchParams.append("chatUuid", chatUuid);
    searchParams.append("anchorMessageUuid", anchorMessageUuid);
    if (projectUuid) searchParams.append("projectUuid", projectUuid);
    if (limit != null) searchParams.append("limit", String(limit));
    if (direction) searchParams.append("direction", direction);
    if (cursorCreatedAt)
      searchParams.append("cursorCreatedAt", cursorCreatedAt);
    if (cursorUuid) searchParams.append("cursorUuid", cursorUuid);
    if (shouldIncludeDeleted) searchParams.append("includeDeleted", "true");
    const response = await this.apiClient.get<IChatThreadWindowResponse>(
      `/analytics/chat-thread-window?${searchParams.toString()}`,
    );
    return response.data;
  }

  async getMessages(
    params: IAnalyticsMessagesParams,
  ): Promise<IPaginatedAnalyticsMessagesResponse> {
    const { messageUuid, limit, offset } = params;
    const searchParams = buildMessagesFilterSearchParams(params);

    if (messageUuid) searchParams.append("messageUuid", messageUuid);
    if (limit !== undefined) searchParams.append("limit", limit.toString());
    if (offset !== undefined) searchParams.append("offset", offset.toString());

    const endpoint = params.hasAllMembersAnalyticsReadPermission
      ? `/analytics/messages/all-members`
      : `/analytics/messages`;
    const query = searchParams.toString();
    const response =
      await this.apiClient.get<IPaginatedAnalyticsMessagesResponse>(
        `${endpoint}${query ? `?${query}` : ""}`,
      );
    return response.data;
  }

  // NOTE: NSAI-7022 — the filtered Messages list as a CSV / XLSX file, through
  // the same filter serialization as getMessages so the file always matches
  // what the list shows.
  async exportMessages(params: IExportAnalyticsMessagesParams): Promise<Blob> {
    const searchParams = buildMessagesFilterSearchParams(params);
    searchParams.append("format", params.format);

    const endpoint = params.hasAllMembersAnalyticsReadPermission
      ? `/analytics/messages/all-members/export`
      : `/analytics/messages/export`;
    const response = await this.apiClient.get<Blob>(
      `${endpoint}?${searchParams.toString()}`,
      { responseType: "blob" },
    );
    return response.data;
  }

  async downloadMessageFile(
    params: IDownloadAnalyticsMessageFileParams,
  ): Promise<Blob> {
    const { fileUuid, projectUuid, scope, onProgress } = params;
    const searchParams = new URLSearchParams();
    if (scope) {
      searchParams.append("scope", scope);
    } else if (projectUuid) {
      searchParams.append("projectUuid", projectUuid);
    }
    const query = searchParams.toString();

    const response = await this.apiClient.get<Blob>(
      `/analytics/message-files/${fileUuid}/download${query ? `?${query}` : ""}`,
      {
        responseType: "blob",
        onDownloadProgress: onProgress
          ? (event) =>
              onProgress({
                loaded: event.loaded,
                total: event.total,
              })
          : undefined,
      },
    );

    return response.data;
  }

  async previewMessageFile(
    params: IPreviewAnalyticsMessageFileParams,
  ): Promise<Blob> {
    const { fileUuid, projectUuid, scope } = params;
    const searchParams = new URLSearchParams();
    if (scope) {
      searchParams.append("scope", scope);
    } else if (projectUuid) {
      searchParams.append("projectUuid", projectUuid);
    }
    const query = searchParams.toString();

    const response = await this.apiClient.get<Blob>(
      `/analytics/message-files/${fileUuid}/preview${query ? `?${query}` : ""}`,
      {
        responseType: "blob",
      },
    );

    return response.data;
  }

  // NOTE: NSAI-6839 — how an uploaded chat attachment was anonymized. Same
  // scope params as the download / preview routes; the server also admits an
  // ANALYTICS_READ-only caller, narrowed to their own files.
  async getMessageFileAnonymization(
    params: IMessageFileAnonymizationParams,
  ): Promise<IMessageFileAnonymization> {
    const { fileUuid, projectUuid, scope } = params;
    const searchParams = new URLSearchParams();
    if (scope) {
      searchParams.append("scope", scope);
    } else if (projectUuid) {
      searchParams.append("projectUuid", projectUuid);
    }
    const query = searchParams.toString();

    const response = await this.apiClient.get<IMessageFileAnonymization>(
      `/analytics/message-files/${fileUuid}/anonymization${query ? `?${query}` : ""}`,
    );

    return response.data;
  }

  async getMessageModelAndProviderOptions(
    params: {
      hasAllMembersAnalyticsReadPermission: boolean;
      memberUuid?: string;
      includeDeleted?: boolean;
    } & IAnalyticsScopeParams,
  ): Promise<IMessageModelAndProviderOptionsResponse> {
    const endpoint = params.hasAllMembersAnalyticsReadPermission
      ? `/analytics/messages/all-members/filter-options`
      : `/analytics/messages/filter-options`;
    const searchParams = new URLSearchParams();
    if (params.hasAllMembersAnalyticsReadPermission && params.memberUuid) {
      searchParams.set("memberUuid", params.memberUuid);
    }
    if (params.projectUuid) {
      searchParams.set("projectUuid", params.projectUuid);
    }
    if (params.scope) searchParams.set("scope", params.scope);
    if (params.hasAllMembersAnalyticsReadPermission && params.includeDeleted) {
      searchParams.set("includeDeleted", "true");
    }
    const query = searchParams.toString();
    const response =
      await this.apiClient.get<IMessageModelAndProviderOptionsResponse>(
        `${endpoint}${query ? `?${query}` : ""}`,
      );
    return response.data;
  }
}
